import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { authApi, User } from '../api/clients/auth';
import { setAuthToken, clearAuthToken, getAuthToken } from '../api/config';
import { setSentryUser, clearSentryUser } from '../utils/sentry';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/storage';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' };

interface AuthContextType extends AuthState {
  loginWithGoogle: (token: string) => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const STORAGE_KEY = 'cw_access_token';

  useEffect(() => {
    // Restore token from storage on boot and then check session
    const existing = safeGetItem(STORAGE_KEY);
    console.log('[AuthContext] Initializing, token in localStorage:', !!existing);
    if (existing && !getAuthToken()) {
      console.log('[AuthContext] Setting token from localStorage');
      setAuthToken(existing);
    }
    console.log('[AuthContext] Checking auth status');
    checkAuth();
  }, []);

  const checkAuth = async () => {
    dispatch({ type: 'AUTH_START' });
    try {
      console.log('[AuthContext] Calling /api/auth/me');
      const response = await authApi.getCurrentUser();
      console.log('[AuthContext] Auth check successful:', response.user.email);
      dispatch({ type: 'AUTH_SUCCESS', payload: response.user });
      
      // Set Sentry user context
      setSentryUser({
        email: response.user.email,
        username: response.user.name,
      });
    } catch (error: any) {
      console.error('[AuthContext] Auth check failed:', error?.response?.status, error?.response?.data);
      dispatch({ type: 'AUTH_ERROR', payload: 'Not authenticated' });
      clearSentryUser();
    }
  };

  const loginWithGoogle = async (token: string) => {
    dispatch({ type: 'AUTH_START' });
    try {
      console.log('[AuthContext] Logging in with Google');
      const response = await authApi.googleSignIn(token);
      console.log('[AuthContext] Google login successful:', response.user.email, 'Token:', !!response.access_token);
      if (response.access_token) {
        setAuthToken(response.access_token);
        const stored = safeSetItem(STORAGE_KEY, response.access_token);
        console.log('[AuthContext] Token set and stored:', stored);
      }
      dispatch({ type: 'AUTH_SUCCESS', payload: response.user });
      
      // Set Sentry user context
      setSentryUser({
        email: response.user.email,
        username: response.user.name,
      });
    } catch (error: any) {
      console.error('[AuthContext] Google login failed:', error);
      const message = error?.response?.data?.error?.message || 'Google sign-in failed';
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  };

  const loginWithEmail = async (email: string, password: string) => {
    dispatch({ type: 'AUTH_START' });
    try {
      const response = await authApi.emailSignIn(email, password);
      if (response.access_token) {
        setAuthToken(response.access_token);
        safeSetItem(STORAGE_KEY, response.access_token);
      }
      dispatch({ type: 'AUTH_SUCCESS', payload: response.user });
      
      // Set Sentry user context
      setSentryUser({
        email: response.user.email,
        username: response.user.name,
      });
    } catch (error: any) {
      const message = error?.response?.data?.error?.message || 'Email sign-in failed';
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
      dispatch({ type: 'LOGOUT' });
      clearAuthToken();
      safeRemoveItem(STORAGE_KEY);
      
      // Clear Sentry user context
      clearSentryUser();
      
      if (window.google?.accounts?.id) {
        window.google.accounts.id.disableAutoSelect();
      }
      
      if (navigator.credentials && navigator.credentials.preventSilentAccess) {
        await navigator.credentials.preventSilentAccess();
      }
    } catch (error) {
      console.error('Logout error:', error);
      dispatch({ type: 'LOGOUT' });
      clearSentryUser();
    }
  };

  const refreshSession = async () => {
    try {
      const response = await authApi.refreshSession();
      if (response.access_token) {
        setAuthToken(response.access_token);
        safeSetItem(STORAGE_KEY, response.access_token);
      }
      dispatch({ type: 'AUTH_SUCCESS', payload: response.user });
    } catch (error) {
      // Do NOT force-logout on refresh failures (e.g., 3rd-party cookies blocked).
      // Keep current session until access token naturally expires; caller may retry later.
      console.warn('Refresh failed; keeping current access token. Error:', error);
      throw error;
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        loginWithGoogle,
        loginWithEmail,
        logout,
        refreshSession,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
